// Adapter layer: shapes assessmentResult + insightfulContent (both real API
// responses) into the single normalized `reportData` view-model
// component.js renders. No scoring/business logic here — every metric,
// score, percentage, and content string passes through as given; the only
// things this layer decides are presentation-adjacent (factor color, and
// unwrapping the content envelope so backend field names never reach JSX).
//
// Trait-key convention: `userTrait.trait.title` is a machine-readable trait
// key (e.g. "boldness"), NOT a display label — content lookups key off this
// same string via `Content.factorBreakdown.factors[traitKey]`. Factors are
// matched by this key, never by array position.
//
// rawScore vs percentage: the real per-trait `score` (e.g. 2.8) lives on a
// 1-4 scale — preserved as `rawScore` and also converted to a 0-100
// `percentage` via `(score - 1) * 100 / 3` below, so every 0-100 visual
// uses `percentage` paired with the explicit `visualMaxScore` constant.
//
// Both the per-factor band and the overall classification are provisional
// (computeProvisionalSociopathClassification, assessment-utils.js) — no
// real nearest_profile/factor-band scoring model exists yet. Replace those
// calls once one does; assessmentResult/insightfulContent already match
// their real contracts, so no shape changes are needed elsewhere here.

import {
  computeProvisionalSociopathOverallPercentage,
  computeProvisionalSociopathClassification,
  normalizeSociopathTraits,
  sociopathScoreToPercentage,
} from "../../utils/assessment-utils";

const FACTOR_COLORS = {
  boldness: "purple",
  meanness: "red",
  disinhibition: "teal",
};

// Denominator for every 0-100 visual (badges, score table, bars) — always
// paired with `percentage`, never with `rawScore` (see rawScore vs
// percentage note above).
const VISUAL_MAX_SCORE = 100;

// Envelope-casing hedge: the verified Swagger contract for this content
// model returns `Content` (PascalCase), but this tolerates a lowercase
// `content` envelope too, in case the live response ever differs from what
// Swagger showed. Exported so dev-tools/report-debug-panel.js can look up
// band/category text for dev-preview overrides without duplicating this.
export const unwrapSociopathContent = (insightfulContent) =>
  insightfulContent.Content ?? insightfulContent.content;

export const mapSociopathReportModel = (
  assessmentResult,
  insightfulContent
) => {
  const content = unwrapSociopathContent(insightfulContent);

  const factors = assessmentResult.userTraits.map((userTrait) => {
    const traitKey = userTrait.trait.title;
    const factor = content.factorBreakdown.factors[traitKey];
    const percentage = sociopathScoreToPercentage(userTrait.score);
    // TEMPORARY provisional per-factor band — the same percentage-bucket
    // rule used for the overall classification below, NOT a real, verified
    // per-factor scoring/band model. Must be the same function Dev Controls
    // uses (dev-tools/report-debug-panel.js) so a given percentage always
    // shows the same band/content whether it comes from the real report or
    // a slider override.
    const band = computeProvisionalSociopathClassification(percentage).band;

    return {
      key: traitKey,
      title: factor?.label,
      rawScore: userTrait.score,
      percentage,
      visualMaxScore: VISUAL_MAX_SCORE,
      // `level` is the internal machine key (styling/content lookups only —
      // never rendered). `levelLabel` is the localized backend display text
      // for that same band (content.factorBreakdown.factors[key].bands[band]
      // .label) — this is what FactorCard/ScoresTable actually show.
      level: band,
      levelLabel: factor?.bands?.[band]?.label,
      color: FACTOR_COLORS[traitKey] || "gray",
      body: factor?.bands?.[band]?.description,
    };
  });

  // categoryProfiles/classificationMethod ("nearest_profile") exist on the
  // real content but are intentionally not implemented yet — the real
  // classification/scoring algorithm doesn't exist in this codebase. The
  // classification below is the TEMPORARY provisional bucket rule instead
  // (see assessment-utils.js — shared with the dashboard overview card).
  // Computed from the raw assessmentResult.userTraits (not the mapped
  // `factors` below) so the exact same function, on the exact same input
  // shape, is callable from the dashboard too. normalizeSociopathTraits is
  // shared with the dashboard overview card so both derive each trait's
  // percentage from its score the same way.
  const overallPercentage = computeProvisionalSociopathOverallPercentage(
    normalizeSociopathTraits(assessmentResult.userTraits)
  );
  const classification =
    computeProvisionalSociopathClassification(overallPercentage);
  const category =
    content.categoryResult.categories[classification.categoryKey];
  const classificationsCount = Object.keys(
    content.categoryResult.categories
  ).length;

  return {
    header: {
      title: content.intro.heading,
      intro: content.intro.content,
    },
    overallResult: {
      score: overallPercentage,
      maxScore: VISUAL_MAX_SCORE,
      // The gauge's own displayed classification text. Uses the localized
      // backend category.shortLabel (falling back to the full label if a
      // given category doesn't have one yet) — never
      // classification.shortLabel from assessment-utils.js, which is a
      // hardcoded English "Non"/"Mild"/"Moderate"/"Severe" string meant
      // only as an internal display hint, not user-facing copy.
      classification: category?.shortLabel || category?.label,
      // Exposed so presentation code (the severity gauge/colors,
      // report-visual-style.js) can key off the already-computed
      // classification directly, instead of re-deriving a band from the
      // percentage a second time. Internal machine key — never rendered.
      band: classification.band,
      classificationLabel: category?.label,
      classificationsCount,
      heading: content.categoryResult.heading,
      paragraphs: category?.paragraphs || [],
      scoresTable: [
        ...factors.map((factor) => ({
          label: factor.title,
          value: `${factor.percentage} / ${factor.visualMaxScore} · ${factor.levelLabel}`,
          // Exposed alongside `value` (unchanged) so presentation code can
          // paint this row from the already-computed band instead of
          // parsing it back out of the formatted string. Internal key —
          // never rendered (the row's displayed text above uses levelLabel).
          band: factor.level,
        })),
        {
          // "Classification" is fixed UI chrome (a row caption, not backend
          // content) — labelKey lets component.js resolve it via t(), same
          // as the Child Autism pattern. `label` is a plain-English fallback
          // only, never the primary rendered value.
          label: "Classification",
          labelKey: "report.sociopath.classification",
          value: category?.label,
          band: classification.band,
        },
      ],
    },
    factors,
    explanation: {
      heading: content.factorBreakdown.heading,
      methodologyLabel: content.howScored.heading,
      methodologyText: content.howScored.content,
    },
    // Sourced from the nested content.howToUse contract (heading/
    // sectionOrder/nextSteps/seekHelp/disclaimer) — sectionOrder isn't
    // consumed here, matching the established convention that no report in
    // this codebase currently reads that field (component.js still renders
    // a fixed section layout).
    insights: {
      heading: content.howToUse.heading,
      nextSteps: {
        heading: content.howToUse.nextSteps.heading,
        items: content.howToUse.nextSteps.items,
      },
      // content.howToUse.seekHelp.addendums exists but is intentionally not
      // used — it's keyed low/moderate/high, a different band vocabulary
      // than categoryResult/factorBreakdown's none/mild/moderate/severe,
      // and mapping between the two isn't specified anywhere in this
      // payload.
      support: {
        heading: content.howToUse.seekHelp.heading,
        body: content.howToUse.seekHelp.content,
      },
      limitations: {
        heading: content.howToUse.disclaimer.heading,
        body: content.howToUse.disclaimer.content,
      },
    },
  };
};
